import { isPossiblePhoneNumber } from "react-phone-number-input";
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { getCountriesWithoutZipcodes } from '../constants/countries';
import { CountryOptions } from '../data/CountryOptions';

export const initialValues = {
  BOLId: '',
  vendorId: '',
  vendorSCAC: '',
  dispatched: false,
  quoteNumber: '', 
  BOLNumber: '',
  PRONmbr: '',
  billTo: {
    referenceNumber: '',
    emails: ''
  },
  shipper: {
    id: '',
    address: '',
    address1: '',
    country: 'US',
    city: '',
    name: '',
    state: '',
    zipcode: '',
    phone: '',
    emails: [],
    contact: '',
    contactPhone: '',
    referenceNumber: '',
    UNLOCode: {}
  },
  consignee: {
    id: '',
    address: '',
    address1: '',
    country: 'US',
    city: '',
    name: '',
    state: '',
    zipcode: '',
    phone: '',
    emails: [],
    contact: '',
    contactPhone: '',
    referenceNumber: '',
    UNLOCode: {}
  },
  freightInfo: [{
      id: uuidv4(),
      qty: '',
      dimType: {
        name: "PALLET",
        value: "PLT"
      },
      container: 20,
      STC: '',
      weight: '',
      each: 'Each',
      L: '',
      W: '',
      H: '',
      class: '',
      commodity: '',
      nmfc: '',
      weightus: '',
      Lus: '',
      Wus: '',
      Hus: '',
      totalCft: '',
      hazmat: false,
      UN: '',
      UNPKGGroup: ''
  }],
  freightInfoUOM: {
      value: '',
      oldValue: ''
  },
  freightInfoTotals: {
      totalPieces: 0,
      totalWeight: 0,
      totalCFT: 0,
      totalDensity: 0,
      symbol: ''
  },
  accessorials: {
      origin: [],
      destination: [],
      other: []
  },
  moneyAccessorialsInformation: [],
  pickupDate: {
      type: 'PickupOn',
      date: null,
      from: null,
      to: null,
      appointmentNeeded: false,
      appointmentDate: null,
      appointmentTime: null,
      appointmentNumber: '',
      appointmentContact: '',
      appointmentNotes: ''
  },
  deliveryDate: {
      type: 'DeliveryBy',
      date: null,
      from: null,
      to: null,
      appointmentNeeded: false,
      appointmentDate: null,
      appointmentTime: null,
      appointmentNumber: '',
      appointmentContact: '',
      appointmentNotes: ''
  },
  specialInstructions: '',
  shipmentNotes: '',
  otherInfo: {
    broker: {
      name: '',
      contact: '',
      phone: '',
      country: 'US',
      instructions: '',
    },
    BKGRefNumber: '',
    BKGValue: '',
    refNumber1: '',
    refNumber2: '',
    incoTerm: '',
    dutyAndTaxPayableBy: '',
    receiverAccountNumber: '',
    exportFiling: '',
    itnNumber: '',
    ftrExemptionCode: '',
    dhlNotificationEmail: '',
    paperlessTrade: false,
    dutiesPaid: false,
  },
  carrierInformation: {
    name: '',
    rateId: '',
    transitDays: '',
    vendorId: null
  },
  emergencyContact: '',
  emergencyPhone: '',
  commodityInsurance: '',
  insuranceAmount: null,
  collect: ''
}
  
  const validatePhoneNumberFormik = (value) => {
    if(value === null){
      return true
    }else if(typeof value === 'string'){
      if(isPossiblePhoneNumber(value)){
          return true
      }else{
          return false
      }
    }else{
      return true
    }
  }

  Yup.addMethod<Yup.DateSchema>(Yup.date, "lessThanToday", function (message) {
    return this.test("test-name", message, function(value: any) {
      const { path, createError } = this
      return ( value < new Date().setHours(0,0,0,0) ? createError({message, path}) : value )
    })
  })

  Yup.addMethod<Yup.AnySchema>(Yup.array, "invalidEmail", function (message) {
    return this.test("test-name", message, function(value: any) {
      const { path, createError } = this
      return ( value?.includes('notanemail') ? createError({message, path}) : value )
    })
  })

  Yup.addMethod<Yup.StringSchema>(Yup.string, "moreThanZero", function (message) {
    return this.test("test-name", message, function(value: any) {
      const { path, createError } = this
      return( typeof value === 'string' ? (parseInt(value) ? value : createError({message, path})) : value > 0 ? value : createError({message, path}))
    })
  })


  export const validationSchema = (values) => {
    return (
      Yup.object().shape({
        shipper: (() => {
          const shipperCountryRaw = values?.formikValues?.shipper?.country || ''
          const shipperCountry = shipperCountryRaw.length === 3 ? (CountryOptions?.find(c => c.CodePrimus === shipperCountryRaw)?.Code || shipperCountryRaw) : shipperCountryRaw
          return Yup.object({
          name: values.dispatchShipment ? Yup.string().required("Required") : Yup.string(),
          address: values.dispatchShipment ? Yup.string().required("Required") : Yup.string(),
          contact: values.dispatchShipment ? Yup.string().required("Required") : Yup.string(),
          city: Yup.string().required('Required'),
          country: Yup.string().required('Required'),
          state: values?.formikValues?.shipper?.State ? Yup.string().required('Required') : Yup.string().nullable(),
          zipcode: getCountriesWithoutZipcodes().includes(shipperCountry) ? Yup.string().nullable() : Yup.string().required('Required'),
          isSL: Yup.boolean(),
          isZipcode: Yup.boolean(),
          isCity: Yup.boolean(),
          phone: values.dispatchShipment && !values.formikValues?.shipper?.contactPhone && Yup.string().required("Required").test("validPhoneNumber", "Invalid phone", (value) => (
            validatePhoneNumberFormik(value)
          )),
          contactPhone: values.dispatchShipment && !values.formikValues?.shipper?.phone && Yup.string().required("Required").test("validPhoneNumber", "Invalid phone", (value) => (
            validatePhoneNumberFormik(value)
          )),
          emails: values.dispatchShipment ? (Yup.array() as any).notRequired().invalidEmail('Invalid') : Yup.array()
        })})(),
        consignee: (() => {
          const consigneeCountryRaw = values?.formikValues?.consignee?.country || ''
          const consigneeCountry = consigneeCountryRaw.length === 3 ? (CountryOptions?.find(c => c.CodePrimus === consigneeCountryRaw)?.Code || consigneeCountryRaw) : consigneeCountryRaw
          return Yup.object({
          name: values.dispatchShipment ? Yup.string().required("Required") : Yup.string(),
          address: values.dispatchShipment ? Yup.string().required("Required") : Yup.string(),
          contact: values.dispatchShipment ? Yup.string().required("Required") : Yup.string(),
          city: Yup.string().required('Required'),
          country: Yup.string().required('Required'),
          state: values?.formikValues?.consignee?.State ? Yup.string().required('Required') : Yup.string().nullable(),
          zipcode: getCountriesWithoutZipcodes().includes(consigneeCountry) ? Yup.string().nullable() : Yup.string().required('Required'),
          isSL: Yup.boolean(),
          isZipcode: Yup.boolean(),
          isCity: Yup.boolean(),
          phone: values.dispatchShipment && !values.formikValues?.consignee?.contactPhone && Yup.string().required("Required").test("validPhoneNumber", "Invalid phone", (value) => (
            validatePhoneNumberFormik(value)
          )), 
          contactPhone: values.dispatchShipment && !values.formikValues?.consignee?.phone && Yup.string().required("Required").test("validPhoneNumber", "Invalid phone", (value) => (
            validatePhoneNumberFormik(value)
          )),
          emails: values.dispatchShipment ? (Yup.array() as any).notRequired().invalidEmail('Invalid') : Yup.array()
        })})(),
        billTo: Yup.object({
          emails: values.dispatchShipment ? (Yup.array() as any).notRequired().invalidEmail('Invalid') : Yup.array()
        }),
        freightInfo: Yup.array().of(
          Yup.object().shape(values?.mode?.code !== 'DRAY' ? 
          values?.mode?.code !== 'VOL' ? {
            commodity: values.dispatchShipment ? Yup.string().required("Required") : Yup.string(),
            volume: Yup.string().nullable(),
            L: values?.requireDimensions ? Yup.string().when('volume', {
              is: (volume) => !volume || parseFloat(volume) <= 0,
              then: (Yup.string() as any).moreThanZero('Required').required(),
              otherwise: Yup.string().nullable()
            }) : Yup.string().nullable(),
            W: values?.requireDimensions ? Yup.string().when('volume', {
              is: (volume) => !volume || parseFloat(volume) <= 0,
              then: (Yup.string() as any).moreThanZero('Required').required(),
              otherwise: Yup.string().nullable()
            }) : Yup.string().nullable(),
            H: values?.requireDimensions ? Yup.string().when('volume', {
              is: (volume) => !volume || parseFloat(volume) <= 0,
              then: (Yup.string() as any).moreThanZero('Required').required(),
              otherwise: Yup.string().nullable()
            }) : Yup.string().nullable(),
            qty: Yup.string().required("Required"),
            weight: Yup.string().required("Required"),
            nmfc: Yup.string().test('length', 'Must be 8 numbers or empty', (value) => {
              if(value && /\s/g.test(value)){
                return false
              }else{
                return true
              }
            }),
          }
          : {
            commodity: values.dispatchShipment ? Yup.string().required("Required") : Yup.string(),
            L: Yup.string(),
            W: Yup.string(),
            H: Yup.string(),
            qty: Yup.string().required("Required"),
            weight: Yup.string().required("Required"),
            nmfc: Yup.string().test('length', 'Must be 8 numbers or empty', (value) => {
              if(value && /\s/g.test(value)){
                return false
              }else{
                return true
              }
            }),
          } : {
            qty: Yup.string().required("Required"),
            weight: Yup.string().required("Required")
          })
        ),
        pickupDate: values.dispatchShipment ? Yup.object().shape({
          date: Yup.date().required("Required"),
          from: Yup.string().required("Required").matches(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/gm),
          to: Yup.string().required("Required").matches(/^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/gm)
        }) : Yup.object().shape({}),
        deliveryDate: Yup.object().shape({}),
      })
    )
  }